import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PairingMatrix from '../hardware/PairingMatrix'
import LiveTelemetryScreen from './LiveTelemetryScreen'
import PostSessionReview from './PostSessionReview'
import type { BleTelemetryController } from '../../lib/hardware/bleManager'
import type { SessionMarkers } from '../../lib/hardware/sessionAggregator'
import type { DiscoveredPeripheral } from '../../lib/hardware/types'

type LiveWorkoutSessionProps = {
  userId: string
  canWearables?: boolean
  onUpgrade?: () => void
  /** Target HR for the active movement profile; drives the intervention trigger. */
  targetHr?: number
  onClose: () => void
  /** Fires after a session is finalized so the parent can refresh health data. */
  onSessionFinalized?: () => void
}

type Phase = 'pair' | 'live' | 'review'

type Result = { markers: SessionMarkers; strainScore: number | null }

/**
 * Full-screen orchestrator for a live machine workout:
 *   pair (PairingMatrix) → live (LiveTelemetryScreen) → review (PostSessionReview)
 * Mounts as an overlay above the app shell and tears itself down on close.
 */
const LiveWorkoutSession = ({
  userId,
  canWearables = false,
  onUpgrade,
  targetHr = 150,
  onClose,
  onSessionFinalized,
}: LiveWorkoutSessionProps) => {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>('pair')
  const [peripheral, setPeripheral] = useState<DiscoveredPeripheral | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const [controller, setController] = useState<BleTelemetryController | null>(null)

  function handleSessionComplete(res: Result) {
    setResult(res)
    setPhase('review')
    onSessionFinalized?.()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/95 backdrop-blur-sm">
      <div className="mx-auto flex min-h-screen max-w-lg flex-col px-4 py-6">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-semibold uppercase tracking-widest text-kilo-accent">
            {phase === 'pair'
              ? t('workout.live.pairEquipment')
              : phase === 'live'
                ? t('workout.live.liveSession')
                : t('workout.live.sessionComplete')}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-neutral-900 px-3 py-1.5 text-xs text-neutral-400 hover:text-white"
          >
            {t('common.close')}
          </button>
        </div>

        {phase === 'pair' ? (
          <PairingMatrix
            userId={userId}
            canWearables={canWearables}
            onUpgrade={onUpgrade}
            onStartLiveSession={(found, liveController) => {
              setController(liveController)
              setPeripheral(found)
              setPhase('live')
            }}
          />
        ) : null}

        {phase === 'live' ? (
          <LiveTelemetryScreen
            targetHr={targetHr}
            machineName={peripheral?.name}
            liveController={controller}
            livePeripheral={peripheral}
            onSessionComplete={handleSessionComplete}
          />
        ) : null}

        {phase === 'review' && result ? (
          <PostSessionReview
            markers={result.markers}
            strainScore={result.strainScore}
            machineName={peripheral?.name}
            onClose={onClose}
          />
        ) : null}
      </div>
    </div>
  )
}

export default LiveWorkoutSession
